import { type TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { type AssetContainer } from '@babylonjs/core/assetContainer';
import { type Scene as BabylonScene } from '@babylonjs/core/scene';
import { type Nullable } from '@babylonjs/core/types';
import { type GarmentGeometryButton, type IScene, type ISceneEntity } from '@seddi/scene';
import { InstancedMeshExt } from '@seddi/ui-babylon-core';
import log from 'loglevel';
import { PieceButton } from '../scene/pieceButton';
import { PieceRenderable } from '../renderables/pieceRenderable';
import * as Scene from '../scene/scene';
import * as SceneModule from '../scene/sceneModule';
import { type ButtonsContext } from '../resources/buttonsContext';
import { type FabricsContext } from '../resources/fabricsContext';
import { type LogosContext } from '../resources/logosContext';
import { type StitchLoader } from './stitchLoader';

export class EcPieceLoader {
  private readonly babylonScene: BabylonScene;
  private readonly stitchLoader: StitchLoader;
  private readonly buttons: ButtonsContext;
  private readonly fabrics: FabricsContext;
  private readonly logos: LogosContext;

  private _assetContainer: Nullable<AssetContainer> = null;

  constructor(
    babylonScene: BabylonScene,
    stitchLoader: StitchLoader,
    buttons: ButtonsContext,
    fabrics: FabricsContext,
    logos: LogosContext,
  ) {
    this.babylonScene = babylonScene;
    this.stitchLoader = stitchLoader;
    this.buttons = buttons;
    this.fabrics = fabrics;
    this.logos = logos;
  }

  async loadAssetContainerAsync(
    scene: IScene,
    entity: ISceneEntity,
    parent: TransformNode,
    container: Nullable<AssetContainer> = null,
  ): Promise<PieceRenderable> {
    this._assetContainer = container;
    return this._loadAsync(scene, entity, parent);
  }

  loadAsync(scene: IScene, entity: ISceneEntity, parent: TransformNode): Promise<PieceRenderable> {
    return this._loadAsync(scene, entity, parent);
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- Todo
  private async _loadAsync(
    scene: IScene,
    entity: ISceneEntity,
    parent: TransformNode,
  ): Promise<PieceRenderable> {
    const component = entity.components.garmentPiece;
    const { fabric: fabricId, fabricColor, grain } = component!;

    // Load geometry
    this.babylonScene._blockEntityCollection = Boolean(this._assetContainer);
    const renderable = new PieceRenderable(entity._id, this.babylonScene, parent);
    // renderable.front.material._parentContainer = this._assetContainer;
    // renderable.front.mesh._parentContainer = this._assetContainer;
    // if (renderable.back) {
    //   renderable.back.material._parentContainer = this._assetContainer;
    //   renderable.back.mesh._parentContainer = this._assetContainer;
    // }
    this.babylonScene._blockEntityCollection = false;
    const hasGeometry = Scene.hasGeometrySurface(scene);

    if (hasGeometry) {
      const garmentGeometry = SceneModule.sceneExt.garmentGeometry;
      if (!garmentGeometry) {
        log.warn(`piece ${entity._id} garment geometry not available`);
        return renderable;
      }
      const piece = garmentGeometry.pieces.find(p => p.id === entity._id);
      const { indices, position2D, position3D } = piece!;
      renderable.setGeometry({ indices, positions: position3D, uvs: position2D });
    } else {
      const { geometry } = component!;
      const { indices, position2D, position3D } = geometry;
      renderable.setGeometry({ indices, positions: position3D, uvs: position2D });
    }

    const bfabric = this.fabrics.get(fabricId, fabricColor);
    renderable.setFabric(bfabric!, grain);

    // Load buttons
    this._loadButtons(entity, scene, parent);

    // Load logos
    this._loadLogos(entity, renderable);

    return renderable;
  }

  /**
   * Loads piece buttons.
   * @param entity - Entity data.
   * @param scene - Scene data.
   * @param parent - Parent node.
   */
  private _loadButtons(entity: ISceneEntity, scene: IScene, parent: TransformNode): void {
    const pieceButtons = entity.components.garmentPiece!.buttons!;
    const { buttons } = scene;
    const hasGeometry = Scene.hasGeometrySurface(scene);

    const geometryButtons = new Map<string, GarmentGeometryButton>();
    if (hasGeometry) {
      for (const button of SceneModule.sceneExt.garmentGeometry!.buttons) {
        geometryButtons.set(button.id, button);
      }
    }

    for (const pieceButton of pieceButtons) {
      const { _id, resourceID: resourceId } = pieceButton;
      const button = buttons.find(({ id }) => id === resourceId)!;

      let transform;
      if (hasGeometry) {
        // 3D transform from geometry surface
        const geometryButton = geometryButtons.get(_id)!;
        transform = geometryButton.transform;
        // Button threads
        const color = pieceButton.recolorThread!;
        for (const thread of geometryButton.threads) {
          this.stitchLoader.loadAssetContainer(_id, thread, color, parent, this._assetContainer);
        }
      } else {
        // 3D transform from 2D transform
        transform = PieceButton.computeTransform(pieceButton, entity, button, scene);
      }

      const babylonButton = this.buttons.get(resourceId)!;
      this.babylonScene._blockEntityCollection = Boolean(this._assetContainer);
      InstancedMeshExt.create(_id, babylonButton.mesh, transform, parent);
      this.babylonScene._blockEntityCollection = false;
    }
  }

  /**
   * Loads piece logos.
   * @param entity - Entity data.
   * @param renderable - The renderable.
   */
  private _loadLogos(entity: ISceneEntity, renderable: PieceRenderable): void {
    const pieceLogos = entity.components.garmentPiece!.logos!;

    for (const pieceLogo of pieceLogos) {
      const { resource: logoId } = pieceLogo;
      const logo = this.logos.get(logoId)!;
      renderable.createLogo(pieceLogo, logo);
    }
  }
}
